import { ThisConnector } from '../../../../../../api/connector'
import { SelectedValueReportingFacet } from '../../../../../../api/queries/value-reporting/vr-vulnerabilities'

type ApiFacetName = 'discovered' | 'exercised' | 'vulnerable'
type UiFacetName = 'routesDiscovered' | 'routesExercised' | 'routesVulnerable'

const apiToUiFacetNameMap: Record<ApiFacetName, UiFacetName> = {
  discovered: 'routesDiscovered',
  exercised: 'routesExercised',
  vulnerable: 'routesVulnerable'
}

export interface VrAggregationQueryArgs {
  organizationUuid: string
  selectedFacets?: SelectedValueReportingFacet[]
}

export interface VrRouteCoverageQueryVariables {
  organizationUuid: string
  selectedFacets?: SelectedValueReportingFacet[]
}

export interface VrRouteCoverageResolverResponse {
  facets?: Record<UiFacetName, number>
}

export interface VrRouteCoverageQueryData {
  valueReportingRouteCoverage: VrRouteCoverageResolverResponse
}

interface VrApiFacet {
  name: ApiFacetName
  displayName: string
  totalCount: number
  values: ApiFacetValue[]
  grade: string
  score: number
}

interface ApiFacetValue {
  value: string
  displayName: string
  count: number
}

const normalizeFacets = (apiFacets: VrApiFacet[]) => {
  if (apiFacets.length > 1) {
    throw new Error(
      'Unexpected API result encountered. Expecting only 1 facet for Route Coverage'
    )
  }

  return apiFacets[0].values.reduce<Record<UiFacetName, number>>(
    (prev, { value, count }) => {
      if (apiToUiFacetNameMap.hasOwnProperty(value)) {
        const uiFacetName = apiToUiFacetNameMap[value]
        return {
          ...prev,
          [uiFacetName]: count
        }
      }
      return prev
    },
    {
      routesDiscovered: 0,
      routesExercised: 0,
      routesVulnerable: 0
    }
  )
}

export async function getVrRouteCoverage(
  this: ThisConnector,
  { organizationUuid, selectedFacets = [] }: VrAggregationQueryArgs
): Promise<VrRouteCoverageResolverResponse> {
  const { facets: responseFacets } = await this.connector.post(
    `/organizations/${organizationUuid}/application-security-summary/routes`,
    { facets: selectedFacets }
  )

  return {
    facets: normalizeFacets(responseFacets)
  }
}
